import { Permissions } from '../auth/permissions'
import { creditNoteViewers } from '../features/support/api'
import type { NavIconName } from './NavIcon'

/**
 * Each module's pages, as this person may open them — one list, read by the sidebar
 * (the current module's pages beneath it) and by the module's own tabs, so the two
 * can never disagree about what is there.
 */
export type SectionPage = { to: string; label: string; end?: boolean }
type Can = (permission: string) => boolean

export function financePages(can: Can): SectionPage[] {
  const items: SectionPage[] = []
  const ledger = can(Permissions.Finance.ViewAccounts)
  const reports = can(Permissions.Finance.ViewReports)
  const expenses = ledger || can(Permissions.Finance.RecordPayment) || can(Permissions.Finance.PettyCashSpend) || can(Permissions.Finance.PettyCashApprove) || can(Permissions.Finance.ApproveExpense)

  if (ledger) items.push({ to: '/finance/accounts', label: 'Accounts' }, { to: '/finance/journals', label: 'Journals' })
  if (reports) items.push({ to: '/finance/trial-balance', label: 'Trial balance' }, { to: '/finance/statements', label: 'Statements' }, { to: '/finance/vat', label: 'VAT' })
  if (expenses) items.push({ to: '/finance/expenses', label: 'Expenses' }, { to: '/finance/petty-cash', label: 'Petty cash' })
  if (can(Permissions.Sales.ViewRegister)) items.push({ to: '/finance/sales-ledger', label: 'Sales ledger' })
  if (ledger || can(Permissions.Finance.RecordReceipt) || can(Permissions.Customer.ViewStatement)) items.push({ to: '/finance/debtors', label: 'Debtors' })
  if (ledger || can(Permissions.Finance.RecordReceipt)) items.push({ to: '/finance/receipts', label: 'Receipts' }, { to: '/finance/mpesa', label: 'M-Pesa' })
  if (can(Permissions.Finance.Reconcile)) items.push({ to: '/finance/bank', label: 'Bank' })
  if (ledger || can(Permissions.Finance.ManageAssets)) items.push({ to: '/finance/assets', label: 'Assets' })
  if (ledger) items.push({ to: '/finance/periods', label: 'Periods' }, { to: '/finance/opening-balances', label: 'Opening balances' })
  return items
}

/** Sales' desks behind the till: the till itself is in the menu of its own (D-115). */
export function salesPages(can: Can): SectionPage[] {
  const items: SectionPage[] = []
  if (can(Permissions.Sales.ViewRegister)) items.push({ to: '/sales/register', label: 'Sales register' })
  if (can(Permissions.Support.View)) items.push({ to: '/sales/support', label: 'Support' })
  if (creditNoteViewers.some((p) => can(p))) items.push({ to: '/sales/credit-notes', label: 'Credit notes' })
  return items
}

/** The Message Center (D-125): campaigns and the automatic messages, who they go to, and what went. */
export function messagesPages(can: Can): SectionPage[] {
  if (!can(Permissions.Messaging.View)) return []
  return [
    { to: '/messages', label: 'Overview', end: true },
    { to: '/messages/campaigns', label: 'Campaigns' },
    { to: '/messages/automations', label: 'Automations' },
    { to: '/messages/segments', label: 'Segments' },
    { to: '/messages/log', label: 'Message log' },
    { to: '/messages/templates', label: 'Templates' },
    { to: '/messages/opt-outs', label: 'Opt-outs & replies' },
  ]
}

export function purchasesPages(can: Can): SectionPage[] {
  const items: SectionPage[] = [
    { to: '/purchases/requisitions', label: 'Requisitions' },
    { to: '/purchases/orders', label: 'Orders' },
    { to: '/purchases/receiving', label: 'Receiving' },
    { to: '/purchases/returns', label: 'Returns' },
  ]
  if (can(Permissions.Purchasing.RecordInvoice) && can(Permissions.Product.ViewCost)) items.push({ to: '/purchases/bills', label: 'Supplier bills' })
  if (can(Permissions.Purchasing.PaySupplier)) items.push({ to: '/purchases/payments', label: 'Payments' })
  if (can(Permissions.Supplier.View)) items.push({ to: '/purchases/supplier-inbox', label: 'Supplier inbox' })
  return items
}

export function warehousePages(can: Can): SectionPage[] {
  const items: SectionPage[] = [
    { to: '/warehouse/orders', label: 'Distribution orders' },
    { to: '/warehouse/indents', label: 'Stock requests' },
    { to: '/warehouse/stock', label: 'Stock' },
    { to: '/warehouse/quarantine', label: 'Quarantine' },
    { to: '/warehouse/adjustments', label: 'Adjustments' },
  ]
  // D-254: only for someone who sees every location — the question it answers is comparative.
  if (can(Permissions.Warehouse.ViewAllLocations)) items.splice(2, 0, { to: '/warehouse/replenishment', label: 'Replenishment' })
  if (can(Permissions.Warehouse.StockTake) || can(Permissions.Warehouse.ApproveAdjustment)) items.push({ to: '/warehouse/stock-takes', label: 'Stock takes' })

  // D-226. Three entries rather than one: the dashboard answers "where do we stand", and the two
  // lists are two different jobs — chasing what we owe, and answering what we have been asked for.
  items.push(
    { to: '/warehouse/loans', label: 'Neighbour stock', end: true },
    { to: '/warehouse/loans/borrowed', label: 'Borrowed from' },
    { to: '/warehouse/loans/lent', label: 'Lent to' },
  )

  if (can(Permissions.Warehouse.ManageAssociates)) items.push({ to: '/warehouse/loans/neighbours', label: 'Neighbours' })
  if (can(Permissions.Supplier.View)) items.push({ to: '/warehouse/suppliers', label: 'Suppliers' })
  return items
}

export function customersPages(can: Can): SectionPage[] {
  const items: SectionPage[] = []
  if (can(Permissions.Customer.View)) items.push({ to: '/customers', label: 'Customers', end: true })
  if (can(Permissions.Crm.ViewLeads)) items.push({ to: '/customers/leads', label: 'Leads' })
  if (can(Permissions.Customer.View)) items.push({ to: '/customers/loyalty', label: 'Loyalty' })
  return items
}

export function productsPages(can: Can): SectionPage[] {
  const items: SectionPage[] = [
    { to: '/products', label: 'Products', end: true },
    { to: '/products/categories', label: 'Categories' },
    { to: '/products/brands', label: 'Makes' },
    { to: '/products/attributes', label: 'Attributes' },
  ]
  if (can(Permissions.Product.Edit)) items.push({ to: '/products/pictures', label: 'Pictures' })
  if (can(Permissions.Product.Import) && can(Permissions.Product.ViewCost)) items.push({ to: '/products/import', label: 'Import' })
  return items
}

export function deliveryPages(can: Can): SectionPage[] {
  const dispatches = can(Permissions.Delivery.UpdateStatus) || can(Permissions.Delivery.AssignRider)
  const items: SectionPage[] = []
  if (can(Permissions.Delivery.ViewBoard) && (dispatches || can(Permissions.Delivery.Create))) items.push({ to: '/logistics', label: 'Board', end: true })
  if (can(Permissions.Delivery.CompleteOwn)) items.push({ to: '/logistics/mine', label: 'My run' })
  if (can(Permissions.Delivery.ViewBoard) && dispatches) items.push({ to: '/logistics/riders', label: 'Riders' })
  if (can(Permissions.Delivery.ViewBoard)) items.push({ to: '/logistics/overview', label: 'Overview' })
  if (can(Permissions.Logistics.ViewShipments)) items.push({ to: '/logistics/shipments', label: 'Shipments' })
  // D-247: the couriers customers' goods are shipped out through. Beside the zones because both
  // answer "how does this reach them", and a page nothing links to is a page nobody finds — which
  // is exactly what happened when this shipped with a route and no menu entry.
  if (can(Permissions.Delivery.ViewBoard)) items.push({ to: '/settings/couriers', label: 'Couriers' })
  if (can(Permissions.Delivery.ViewBoard)) items.push({ to: '/settings/delivery-zones', label: 'Zones & fees' })
  return items
}

/** User Master (D-134): the staff's accounts, and the roles they are given. */
export function usersPages(can: Can): SectionPage[] {
  const items: SectionPage[] = []
  if (can(Permissions.Administration.ManageUsers)) items.push({ to: '/users', label: 'Staff accounts', end: true })
  if (can(Permissions.Administration.ManageRoles)) items.push({ to: '/users/roles', label: 'Roles' })
  if (can(Permissions.Administration.ManageUsers)) items.push({ to: '/users/cover', label: 'Working elsewhere' })
  return items
}

/** A module's pages, by the menu item they sit under (its icon: its destination varies by person). */
export const sectionsByModule: Partial<Record<NavIconName, (can: Can) => SectionPage[]>> = {
  finance: financePages,
  sales: salesPages,
  purchases: purchasesPages,
  warehouse: warehousePages,
  customers: customersPages,
  products: productsPages,
  delivery: deliveryPages,
  messages: messagesPages,
  users: usersPages,
}

/** Whether a page is the one on screen. */
export function isCurrentPage(page: SectionPage, pathname: string): boolean {
  return page.end ? pathname === page.to : pathname === page.to || pathname.startsWith(`${page.to}/`)
}
